import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, Calculator, TrendingUp } from 'lucide-react';
import { useDetectorKeywordsReportQuery } from '@/lib/query-options';
import { Spinner } from '@/components/ui/spinner';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';

interface TCPADetectorData {
  id: number;
  name: string;
  keywords: boolean; // True if eligible, false if not
  campaign_averages: {
    current_tcpa?: number;
    calculated_tcpa?: number;
    conversions?: number;
    cost?: number;
    period_days?: number;
    recommended_tcpa?: number;
    eligibility_reason?: string;
  };
}

export const TCPADetectorDashboard = ({
  campaignId,
  detector,
  detectorLabel,
}: {
  campaignId?: number;
  detector?: string;
  detectorLabel?: string;
}) => {
  const {
    data: reportData,
    isLoading,
    error,
  } = useDetectorKeywordsReportQuery(campaignId || 0, detector || '') as {
    data: TCPADetectorData | undefined;
    isLoading: boolean;
    error: Error | null;
  };

  const { symbol: currencySymbol } = useCurrencySymbol('google');

  if (isLoading) {
    return (
      <Card className='p-6 bg-white border'>
        <div className='flex items-center justify-center p-8'>
          <Spinner />
          <span className='ml-2'>Calculating TCPA values...</span>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className='p-6 bg-white border'>
        <Alert className='border-red-200 bg-red-50'>
          <XCircle className='h-4 w-4' />
          <AlertDescription>
            <strong>Error calculating TCPA:</strong> {error.message}
          </AlertDescription>
        </Alert>
      </Card>
    );
  }

  if (!reportData) {
    return (
      <Card className='p-6 bg-white border'>
        <Alert>
          <AlertDescription>No TCPA data available for this campaign.</AlertDescription>
        </Alert>
      </Card>
    );
  }

  const { campaign_averages: tcpaData, keywords: isEligible } = reportData;
  const conversions = tcpaData?.conversions || 0;
  const currentTcpa = tcpaData?.current_tcpa || 0; // Database TCPA
  const calculatedTcpa = tcpaData?.calculated_tcpa || 0; // Calculated from cost/conversions
  const recommendedTcpa = tcpaData?.recommended_tcpa || 100; // Default fixed value
  const periodDays = tcpaData?.period_days || 30;
  const cost = tcpaData?.cost || 0;

  return (
    <Card className='p-6 bg-white border'>
      <div className='space-y-6'>
        <div className='flex items-center justify-between'>
          <CardHeader className='p-0'>
            <CardTitle className='text-lg font-medium'>{detectorLabel}</CardTitle>
          </CardHeader>
          <Badge variant={isEligible ? 'default' : 'secondary'} className='ml-2'>
            {isEligible ? (
              <>
                <CheckCircle className='w-3 h-3 mr-1' />
                Eligible
              </>
            ) : (
              <>
                <XCircle className='w-3 h-3 mr-1' />
                Not Eligible
              </>
            )}
          </Badge>
        </div>

        <CardContent className='p-0 space-y-4'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {/* Eligibility Status */}
            <div className='p-4 bg-gray-50 rounded-lg'>
              <div className='flex items-center space-x-2 mb-2'>
                <Calculator className='h-4 w-4 text-gray-700' />
                <span className='text-sm font-medium text-gray-700'>TCPA Eligibility</span>
              </div>
              <div className='space-y-2'>
                <div className='flex justify-between items-center'>
                  <span className='text-sm'>Conversions ({periodDays} days):</span>
                  <span className={`font-medium ${conversions >= 30 ? 'text-green-600' : 'text-red-600'}`}>
                    {conversions}
                  </span>
                </div>
                <div className='flex justify-between items-center'>
                  <span className='text-sm'>Minimum Required:</span>
                  <span className='font-medium'>30</span>
                </div>
                <div className='text-xs text-gray-500 mt-2'>
                  {isEligible
                    ? '✓ Campaign has sufficient conversions for TCPA optimization'
                    : '✗ Campaign needs more conversions for reliable TCPA optimization'}
                </div>
              </div>
            </div>

            {/* Current Performance */}
            <div className='p-4 bg-gray-50 rounded-lg'>
              <div className='flex items-center space-x-2 mb-2'>
                <TrendingUp className='h-4 w-4 text-gray-700' />
                <span className='text-sm font-medium text-gray-700'>Current Performance</span>
              </div>
              <div className='space-y-2'>
                <div className='flex justify-between items-center'>
                  <span className='text-sm'>Total Cost:</span>
                  <span className='font-medium'>
                    {currencySymbol}
                    {cost.toFixed(2)}
                  </span>
                </div>
                <div className='flex justify-between items-center'>
                  <span className='text-sm'>Total Conversions:</span>
                  <span className='font-medium'>{conversions}</span>
                </div>
                <div className='flex justify-between items-center'>
                  <span className='text-sm'>Calculated TCPA:</span>
                  <span className='font-medium text-blue-600'>
                    {conversions > 0 ? `${currencySymbol}${calculatedTcpa.toFixed(2)}` : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* TCPA Values */}
          <div className='p-4 border-2 border-blue-100 bg-blue-50 rounded-lg'>
            <h3 className='text-lg font-semibold text-blue-900 mb-4'>TCPA Values</h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div className='text-center'>
                <div className='text-sm text-gray-700 mb-1'>Current TCPA</div>
                <div className='text-2xl font-bold text-gray-900'>
                  {conversions > 0 ? `${currencySymbol}${currentTcpa.toFixed(2)}` : 'N/A'}
                </div>
                <div className='text-xs text-gray-500'>{conversions > 0 ? '' : 'Insufficient conversions'}</div>
              </div>
              <div className='text-center'>
                <div className='text-sm text-blue-600 mb-1'>Recommended TCPA</div>
                <div className='text-2xl font-bold text-blue-600'>
                  {currencySymbol}
                  {(recommendedTcpa / 100).toFixed(2)}
                </div>
                <div className='text-xs text-blue-500'>Value that would be set</div>
              </div>
            </div>
          </div>

          {/* Status Alert */}
          {!isEligible && (
            <Alert className='border-yellow-200 bg-yellow-50'>
              <AlertDescription>
                <strong>Action Required:</strong> Campaign needs at least {30 - conversions} more conversions in the
                last {periodDays} days before TCPA optimization can be enabled.
              </AlertDescription>
            </Alert>
          )}

          {isEligible && (
            <Alert className='border-green-200 bg-green-50'>
              <AlertDescription>
                <strong>Ready for Optimization:</strong> Campaign is eligible for TCPA optimization. The system would
                set TCPA to {currencySymbol}
                {(recommendedTcpa / 100).toFixed(2)} if executed.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </div>
    </Card>
  );
};
